import { storeToRefs } from 'pinia'

import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { useCartStore } from '~/stores/cart'
import { useDraftStore } from '~/stores/draft'
import { useDraftPricingStore } from '~/stores/draftPricing'
import { useInfluencersStore } from '~/stores/influencers'
import { useMiscResizeStore } from '~/stores/miscResize'
import { useSnackbarStore } from '~/stores/snackbar'
import { useUserStore } from '~/stores/user'

import { provideRecomputeCartPrice } from '~/api-core/Cart/FetchCart'

import { Breakpoints } from '~/enums/breakpoints'

import type { StatsV3Influencer } from '~/types/influencer'

export type DraftOrigin = 'payin' | 'recap'

export function useDraftRefresh() {
  const { $pinia } = useNuxtApp()
  const { t } = useI18n()
  const localePath = useLocalePath()

  const { coreFetch } = useProvideCoreFetch()

  const draftStore = useDraftStore($pinia)
  const { GET_BY_IDS_SORTED: GET_INF_SORTED_BY_IDS } =
    useInfluencersStore($pinia)
  const { NEED_PURCHASE } = storeToRefs(useDraftPricingStore($pinia))
  const { id: DRAFT_ID, HAS_DRAFT } = storeToRefs(draftStore)
  const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore($pinia))
  const { id: CART_ID } = storeToRefs(useCartStore($pinia))

  const { CREATE: CREATE_SNACKBAR } = useSnackbarStore($pinia)
  const { FETCH_SET, REMOVE_INFLUENCERS_IDS: REMOVE_INFLUENCER_IDS } =
    useInfluencersStore($pinia)
  const {
    FETCH_UPDATED_INFLUENCERS,
    REMOVE_INF,
    REMOVE_INFLUENCER_ARRAY: REMOVE_INFLUENCER_ARRAY_FROM_DRAFT,
    RESET,
    SAVE_DRAFT_TO_LOCAL_STORAGE,
    UPDATE: UPDATE_DRAFT_ON_SERVER,
  } = draftStore
  const { SET: SET_CART_CHECKOUT } = useCartCheckoutStore($pinia)
  const { SET_CART } = useCartStore($pinia)

  const { UPDATE_GROOVIZ } = useUserStore($pinia)

  function saveTemporaryDraft() {
    if (HAS_DRAFT.value) return
    SAVE_DRAFT_TO_LOCAL_STORAGE()
    RESET()
  }

  async function draftRefresh({
    from = 'payin',
    neededGroovizBeforeRefresh = true,
  }: {
    from: DraftOrigin
    neededGroovizBeforeRefresh: boolean
  }) {
    let ret = true as boolean

    const { influencersNoLongerAvailable = [], influencersWithNewCost = [] } =
      await FETCH_UPDATED_INFLUENCERS().catch(() => {
        ret = false
        CREATE_SNACKBAR({
          props: {
            text: t('errors.draftInfluencersRefresh'),
            icon: 'fas fa-bug',
          },
        })
        return {
          influencersNoLongerAvailable: [],
          influencersWithNewCost: [],
        }
      })

    if (ret === false) return ret

    if (influencersNoLongerAvailable.length) {
      ret = false
      await handleInfluencersNoLongerAvailable(influencersNoLongerAvailable)
    }

    if (influencersWithNewCost.length) {
      ret = false
      handleInfluencersWithNewCost(influencersWithNewCost)
    }

    if ((await isWalletSynced()) === false) {
      ret = false
      handleUnsyncedWallet()
    }

    if (ret === false) {
      handleAmountToPayChanged({ from, neededGroovizBeforeRefresh })
      const recomputeCartPrice = provideRecomputeCartPrice(coreFetch)

      const { cart, checkout } = await recomputeCartPrice(CART_ID.value).catch(
        () => ({
          cart: null,
          checkout: null,
        }),
      )

      if (cart) SET_CART(cart)
      if (checkout) SET_CART_CHECKOUT(checkout)
    }

    UPDATE_DRAFT_ON_SERVER()
    return ret
  }

  async function isWalletSynced(): Promise<boolean> {
    const { oldUserGrooviz, newUserGrooviz } = await UPDATE_GROOVIZ()
    return newUserGrooviz === oldUserGrooviz
  }

  function handleUnsyncedWallet() {
    CREATE_SNACKBAR({
      props: {
        text: t('hub.recap.snackbar.walletChanged'),
        icon: 'fas fa-exclamation-triangle',
      },
    })
  }

  async function handleInfluencersNoLongerAvailable(
    influencersUpdated: number[],
  ): Promise<void> {
    const influencers = GET_INF_SORTED_BY_IDS(influencersUpdated)

    REMOVE_INFLUENCER_ARRAY_FROM_DRAFT(influencersUpdated)
    await REMOVE_INF(influencersUpdated)
    influencers.forEach((influencer: StatsV3Influencer) => {
      CREATE_SNACKBAR({
        props: {
          text: t('hub.recap.snackbar.influencerNoLongerAvailable', {
            name: influencer.entity,
          }),
          icon: 'fas fa-exclamation-triangle',
        },
      })
    })
  }

  async function handleInfluencersWithNewCost(
    influencersWithNewCost: number[],
  ): Promise<any> {
    const influencers = GET_INF_SORTED_BY_IDS(influencersWithNewCost)

    REMOVE_INFLUENCER_IDS(influencersWithNewCost)
    await FETCH_SET(influencersWithNewCost)
    influencers.forEach((influencer: StatsV3Influencer) => {
      CREATE_SNACKBAR({
        props: {
          text: t('hub.recap.snackbar.influencerWithNewCost', {
            name: influencer.entity,
          }),
          icon: 'fas fa-exclamation-triangle',
        },
      })
    })
  }

  function handleAmountToPayChanged({
    from = 'payin',
    neededGroovizBeforeRefresh = true,
  }: {
    from: DraftOrigin
    neededGroovizBeforeRefresh: boolean
  }) {
    if (from === 'recap') {
      if (!neededGroovizBeforeRefresh) {
        CREATE_SNACKBAR({
          props: {
            text: NEED_PURCHASE.value
              ? t('hub.recap.snackbar.notEnoughGrooviz')
              : t('band.payin.snackbar.campaignCostUpdated'),
            icon: 'fas fa-exclamation-triangle',
          },
        })
      } else {
        CREATE_SNACKBAR({
          props: {
            text: NEED_PURCHASE.value
              ? t('band.payin.snackbar.amountToPayUpdated')
              : t('hub.recap.snackbar.enoughGrooviz'),
            icon: 'fas fa-exclamation-triangle',
          },
        })
      }
    } else if (from === 'payin') {
      if (NEED_PURCHASE.value) {
        CREATE_SNACKBAR({
          props: {
            text: t('band.payin.snackbar.amountToPayUpdated'),
            icon: 'fas fa-exclamation-triangle',
          },
        })
      } else {
        CREATE_SNACKBAR({
          props: {
            text: t('band.payin.snackbar.youWillBeRedirected'),
            icon: 'fas fa-exclamation-triangle',
          },
        })

        return navigateTo(
          localePath({
            path:
              SCREEN_WIDTH.value < Breakpoints.lg
                ? `/draft/${DRAFT_ID.value}/recap/`
                : `/draft/${DRAFT_ID.value}/`,
            query: {
              step: SCREEN_WIDTH.value >= Breakpoints.lg ? '3' : undefined,
              snackbar: 'enoughGrooviz',
            },
          }),
        )
      }
    }
  }

  return { draftRefresh, saveTemporaryDraft } as const
}
